import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import { GetTransferRes } from "gen/api/transfer/service_pb"
import { Location } from "gen/company/models_pb"
import { Timestamp as TimestampPB } from "gen/google/protobuf/timestamp_pb"
import { LedgerEntry_UomSnapshot } from "gen/ledger/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import {
  Transfer_Product as TransferProduct,
  Transfer_Status as TransferStatus,
} from "gen/txn/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import { UUID } from "shared-ts/classes/uuid"
import {
  convertUoMQuantityToQuantity,
  getConversionFactorForUom,
} from "shared-ts/libs/product/unitsOfMeasure.helpers"

import { AppState } from "app/store"
import { stringify } from "lib/stringify"

import {
  applyTransferUomSelection,
  getTransferDefaultUomId,
  getTransferUomInfo,
} from "./helpers"
import TransferForm from "./types/TransferForm"
import {
  checkProductsEquality,
  fixTransferProductsOrders,
} from "../transactions/helpers"
import { TransactionProduct } from "../transactions/transactionProduct"

export interface TransferFormState extends TransferForm {
  initialState?: TransferForm
  isEdited?: boolean
  // Prevent updating both requested/picked or picked/received quantities
  // in the same transfer state transition for stock status tracking
  requireQuantitySave?: boolean
  isPreloadingProducts?: boolean
}

const initialState: TransferFormState = {
  notes: "",
  transferToLocation: undefined,
  transferFromLocation: undefined,
  isEdited: false,
  searchProduct: undefined,
  addProductQuantity: Decimal.fromBigSource(1),
  requestDate: undefined,
  requireQuantitySave: undefined,
  isPreloadingProducts: false,
}

const checkTransferIsEdited = (state: TransferFormState) => {
  const initState = { ...state.initialState }
  const currState = { ...state, isEdited: undefined, initialState: undefined }

  return !checkStateEquality(initState ?? {}, currState)
}

const checkStateEquality = (
  stateA: TransferFormState,
  stateB: TransferFormState
) =>
  UUID.eqFromPB(
    stateA.transferFromLocation?.id,
    stateB.transferFromLocation?.id
  ) &&
  UUID.eqFromPB(stateA.transferToLocation?.id, stateB.transferToLocation?.id) &&
  checkProductsEquality(
    Object.values(stateA.transferProductsById || {}),
    Object.values(stateB.transferProductsById || {})
  ) &&
  stateA.notes === stateB.notes &&
  stringify(stateA.searchProduct) === stringify(stateB.searchProduct) &&
  (stateA.addProductQuantity && stateB.addProductQuantity
    ? stateA.addProductQuantity.eq(stateB.addProductQuantity)
    : true) &&
  TimestampPB.equals(stateA.requestDate, stateB.requestDate)

function seedProductsById(
  productsById?: Record<string, TransferProduct>,
  status?: TransferStatus
) {
  if (status !== TransferStatus.REQUESTED && status !== TransferStatus.SENT) {
    return productsById
  }

  if (!productsById) {
    return {}
  }

  const seededProductsById: Record<string, TransferProduct> = {}
  for (const [id, product] of Object.entries(productsById)) {
    switch (status) {
      case TransferStatus.REQUESTED:
        seededProductsById[id] = {
          ...product,
          quantitySentV2: product.quantitySentV2 ?? product.quantityRequestedV2,
          quantitySentUom:
            product.quantitySentUom ?? product.quantityRequestedUom,
        }
        continue
      case TransferStatus.SENT:
        seededProductsById[id] = {
          ...product,
          quantityReceivedV2: product.quantitySentV2,
          quantityReceivedUom: product.quantitySentUom,
        }
        continue
      default:
        seededProductsById[id] = { ...product }
        continue
    }
  }

  return seededProductsById
}

export const transfersSlice = createSlice({
  name: "transfers",
  initialState,
  reducers: {
    setTransferInitialState: (state, action: PayloadAction<GetTransferRes>) => {
      const seededTransferProductsById = seedProductsById(
        action.payload.transfer?.productsById,
        action.payload.transfer?.status
      )
      const normalizedTransferProductsById: Record<string, TransferProduct> = {}
      for (const [id, tp] of Object.entries(seededTransferProductsById ?? {})) {
        normalizedTransferProductsById[id] = applyTransferUomSelection({
          transferProduct: tp,
        })
      }

      state.initialState = {
        inventoriesByProductId: action.payload.inventoriesByProductId,
        productsById: action.payload.productsById,
        catalogProductsById: action.payload.catalogProductsById,
        notes: action.payload.transfer?.notes,
        transferFromLocation: action.payload.fromLocation,
        transferToLocation: action.payload.toLocation,
        transferProductsById: normalizedTransferProductsById,
      }
      state.inventoriesByProductId = action.payload.inventoriesByProductId
      state.isEdited = false
      state.productsById = action.payload.productsById
      state.catalogProductsById = action.payload.catalogProductsById
      state.notes = action.payload.transfer?.notes
      state.transferFromLocation = action.payload.fromLocation
      state.transferProductsById = normalizedTransferProductsById
      state.transferToLocation = action.payload.toLocation
      state.searchProduct = undefined
      state.addProductQuantity = Decimal.fromBigSource(1)
      state.requestDate = action.payload.transfer?.requestDate
      state.requireQuantitySave = undefined
    },
    resetTransferState: (state) => {
      if (!state.isPreloadingProducts) {
        state.transferProductsById = {}
        state.productsById = {}
        state.catalogProductsById = {}
        state.inventoriesByProductId = {}
        state.isEdited = false
      } else {
        state.isEdited = true
      }
      state.notes = ""
      state.transferToLocation = undefined
      state.transferFromLocation = undefined
      state.searchProduct = undefined
      state.addProductQuantity = Decimal.fromBigSource(1)
      state.requestDate = undefined
      state.requireQuantitySave = undefined
      state.isPreloadingProducts = false
    },
    setTransferFromLocation: (
      state,
      action: PayloadAction<Location | undefined>
    ) => {
      if (
        !!action.payload?.id &&
        UUID.eqFromPB(action.payload?.id, state.transferToLocation?.id)
      ) {
        return
      }
      state.transferFromLocation = action.payload
      state.isEdited = checkTransferIsEdited(state)
    },
    setTransferToLocation: (
      state,
      action: PayloadAction<Location | undefined>
    ) => {
      if (
        !!action.payload?.id &&
        UUID.eqFromPB(action.payload?.id, state.transferFromLocation?.id)
      ) {
        return
      }
      state.transferToLocation = action.payload
      state.isEdited = checkTransferIsEdited(state)
    },
    setTransferNotes: (state, action: PayloadAction<string | undefined>) => {
      state.notes = action.payload
      state.isEdited = checkTransferIsEdited(state)
    },
    setTransferRequestDate: (
      state,
      action: PayloadAction<TimestampPB | undefined>
    ) => {
      state.requestDate = action.payload
      state.isEdited = checkTransferIsEdited(state)
    },
    setTransferProducts: (
      state,
      action: PayloadAction<Record<string, TransferProduct>>
    ) => {
      const next: Record<string, TransferProduct> = {}
      for (const [id, tp] of Object.entries(action.payload ?? {})) {
        next[id] = applyTransferUomSelection({ transferProduct: tp })
      }
      state.transferProductsById = next
      state.isEdited = checkTransferIsEdited(state)
    },
    setTransferProductQuantityRequested: (
      state,
      action: PayloadAction<TransferProduct>
    ) => {
      if (!action.payload.productId) {
        return
      }
      const payloadId = UUID.fromPB(action.payload.productId).toString()
      if (!state.transferProductsById?.[payloadId]) {
        return
      }

      state.transferProductsById![payloadId] = {
        ...state.transferProductsById![payloadId],
        quantityRequestedV2: action.payload.quantityRequestedV2,
      }
      state.transferProductsById![payloadId] = applyTransferUomSelection({
        transferProduct: state.transferProductsById![payloadId],
      })

      const anyRequestedProductQuantityDiffers = Object.entries(
        state.transferProductsById ?? {}
      ).some(
        ([id, product]) =>
          product.quantityRequestedV2 &&
          !Decimal.fromPB(product.quantityRequestedV2).eq(
            Decimal.fromPB(
              state.initialState?.transferProductsById?.[id]
                ?.quantityRequestedV2 ?? Decimal.zero()
            )
          )
      )

      state.requireQuantitySave =
        anyRequestedProductQuantityDiffers ?? undefined

      state.isEdited = checkTransferIsEdited(state)
    },
    setTransferProductQuantitySent: (
      state,
      action: PayloadAction<TransferProduct>
    ) => {
      if (!action.payload.productId) {
        return
      }
      const payloadId = UUID.fromPB(action.payload.productId).toString()
      if (!state.transferProductsById?.[payloadId]) {
        return
      }

      state.transferProductsById![payloadId] = {
        ...state.transferProductsById![payloadId],
        quantitySentV2: action.payload.quantitySentV2,
      }
      state.transferProductsById![payloadId] = applyTransferUomSelection({
        transferProduct: state.transferProductsById![payloadId],
      })

      const anyPickedProductQuantityDiffers = Object.entries(
        state.transferProductsById ?? {}
      ).some(
        ([id, product]) =>
          product.quantitySentV2 &&
          !Decimal.fromPB(product.quantitySentV2).eq(
            Decimal.fromPB(
              state.initialState?.transferProductsById?.[id]?.quantitySentV2 ??
                Decimal.zero()
            )
          )
      )

      state.requireQuantitySave = anyPickedProductQuantityDiffers ?? undefined

      state.isEdited = checkTransferIsEdited(state)
    },
    setTransferProductQuantity: (
      state,
      action: PayloadAction<TransferProduct>
    ) => {
      if (!action.payload.productId) {
        return
      }

      const payloadId = UUID.fromPB(action.payload.productId).toString()
      if (!state.transferProductsById?.[payloadId]) {
        return
      }

      state.transferProductsById![payloadId] = {
        ...state.transferProductsById![payloadId],
        quantityReceivedV2: action.payload.quantityReceivedV2,
        quantitySentV2: action.payload.quantitySentV2,
        quantityRequestedV2: action.payload.quantityRequestedV2,
      }
      state.transferProductsById![payloadId] = applyTransferUomSelection({
        transferProduct: state.transferProductsById![payloadId],
      })

      state.isEdited = checkTransferIsEdited(state)
    },
    setTransferProductUom: (
      state,
      action: PayloadAction<{
        productId: PBUUID
        uomId?: PBUUID
        uomSnapshot?: LedgerEntry_UomSnapshot
      }>
    ) => {
      const { productId, uomId, uomSnapshot } = action.payload
      const payloadId = UUID.fromPB(productId).toString()
      if (!state.transferProductsById?.[payloadId]) {
        return
      }

      state.transferProductsById[payloadId] = applyTransferUomSelection({
        transferProduct: {
          ...state.transferProductsById[payloadId],
          uomId,
          uomSnapshot,
        },
      })

      state.isEdited = checkTransferIsEdited(state)
    },
    setNewTransferProduct: (state, action: PayloadAction<TransferProduct>) => {
      if (!action.payload.productId) {
        return
      }

      if (!state.transferProductsById) {
        state.transferProductsById = {}
      }

      const payloadId = UUID.fromPB(action.payload.productId).toString()

      state.transferProductsById[payloadId] = action.payload
      state.isEdited = checkTransferIsEdited(state)
    },
    removeTransferProduct: (state, action: PayloadAction<string>) => {
      if (state.transferProductsById?.[action.payload]) {
        delete state.transferProductsById[action.payload]
        state.transferProductsById = fixTransferProductsOrders(
          state.transferProductsById
        )
      }

      if (state.productsById?.[action.payload]) {
        delete state.productsById[action.payload]
      }

      if (state.catalogProductsById?.[action.payload]) {
        delete state.catalogProductsById[action.payload]
      }

      if (state.inventoriesByProductId?.[action.payload]) {
        delete state.inventoriesByProductId[action.payload]
      }

      state.isEdited = checkTransferIsEdited(state)
    },
    searchProductSelected: (
      state,
      action: PayloadAction<{
        result: TransactionProduct
        status?: TransferStatus
        // Only the hardware-scanner path auto-submits. Search-combobox and
        // AddProductModal selections also produce isScan results (they share
        // the scan RPC) but must stay select-only.
        isPhysicalScan?: boolean
      }>
    ) => {
      selectTransferProduct(state, action.payload.result)

      const result = action.payload.result
      if (
        action.payload.isPhysicalScan &&
        !result.product?.isSpecialOrderableV2 &&
        !result.product?.isNonStock
      ) {
        transfersSlice.caseReducers.searchProductSubmitted(state, {
          type: "searchProductSubmitted",
          payload: {
            status: action.payload.status,
            quantity: result.quantity.gt(Decimal.zero())
              ? result.quantity
              : Decimal.fromBigSource(1),
            uomId: result.uomId,
            uomSnapshot: result.uomSnapshot,
          },
        })
      }
    },
    searchProductSubmitted: (
      state,
      action: PayloadAction<{
        status?: TransferStatus
        quantity?: Decimal
        uomId?: PBUUID
        uomSnapshot?: LedgerEntry_UomSnapshot
      }>
    ) => {
      submitTransferProduct(
        state,
        action.payload.status,
        action.payload.uomId,
        action.payload.uomSnapshot,
        action.payload.quantity
      )
    },
    resetSearchProductSelected: (state) => {
      delete state.searchProduct
      state.isEdited = checkTransferIsEdited(state)
    },
    setAddProductQuantity: (state, action: PayloadAction<Decimal>) => {
      state.addProductQuantity = action.payload
      state.isEdited = checkTransferIsEdited(state)
    },
    setIsPreloadingProducts: (state, action: PayloadAction<boolean>) => {
      state.isPreloadingProducts = action.payload
    },
    preloadProducts: (
      state,
      action: PayloadAction<{
        products: TransactionProduct[]
      }>
    ) => {
      const { products } = action.payload
      for (const product of products) {
        selectTransferProduct(state, product)
        submitTransferProduct(state, TransferStatus.UNDEFINED)
      }
      state.isPreloadingProducts = true
    },
  },
})

export const {
  removeTransferProduct,
  resetTransferState,
  setNewTransferProduct,
  setTransferFromLocation,
  setTransferInitialState,
  setTransferProducts,
  setTransferNotes,
  setTransferToLocation,
  searchProductSelected,
  setTransferProductQuantity,
  setTransferProductUom,
  searchProductSubmitted,
  resetSearchProductSelected,
  setAddProductQuantity,
  setTransferRequestDate,
  setTransferProductQuantityRequested,
  setTransferProductQuantitySent,
  setIsPreloadingProducts,
  preloadProducts,
} = transfersSlice.actions

export const selectTransferFromLocation = (state: AppState) =>
  state.transfers.transferFromLocation
export const selectTransferInitialState = (state: AppState) =>
  state.transfers.initialState
export const selectTransferInventoriesByProductId = (state: AppState) =>
  state.transfers.inventoriesByProductId
export const selectTransferIsEdited = (state: AppState) =>
  state.transfers.isEdited
export const selectTransferProducts = (state: AppState) =>
  state.transfers.transferProductsById
export const selectTransferProductsById = (state: AppState) =>
  state.transfers.productsById
export const selectTransferCatalogProductsById = (state: AppState) =>
  state.transfers.catalogProductsById
export const selectTransferNotes = (state: AppState) => state.transfers.notes
export const selectTransferToLocation = (state: AppState) =>
  state.transfers.transferToLocation
export const selectSearchProduct = (state: AppState) =>
  state.transfers.searchProduct
export const selectAddProductQuantity = (state: AppState) =>
  state.transfers.addProductQuantity
export const selectTransferRequestDate = (state: AppState) =>
  state.transfers.requestDate
export const selectTransferRequireQuantitySave = (state: AppState) =>
  state.transfers.requireQuantitySave

function selectTransferProduct(
  state: TransferFormState,
  result: TransactionProduct
) {
  if (!result || !result?.product?.id) {
    return
  }
  state.searchProduct = result

  if (result.isScan && result.quantity.gt(Decimal.zero())) {
    state.addProductQuantity = result.quantity
  }

  state.isEdited = checkTransferIsEdited(state)
}

function submitTransferProduct(
  state: TransferFormState,
  status?: TransferStatus,
  submittedUomId?: PBUUID,
  submittedUomSnapshot?: LedgerEntry_UomSnapshot,
  quantity?: Decimal
) {
  const uomQuantityToAdd = quantity ?? state.addProductQuantity
  if (!uomQuantityToAdd || !state.searchProduct?.product?.id) {
    return
  }

  if (!state.transferProductsById) {
    state.transferProductsById = {}
  }

  if (!state.productsById) {
    state.productsById = {}
  }

  if (!state.catalogProductsById) {
    state.catalogProductsById = {}
  }

  if (!state.inventoriesByProductId) {
    state.inventoriesByProductId = {}
  }

  const payloadId = UUID.fromPB(state.searchProduct.product.id).toString()

  const existingTransferProduct = state.transferProductsById?.[payloadId]

  const existingFactor = existingTransferProduct
    ? getTransferUomInfo({ transferProduct: existingTransferProduct })
        .conversionFactorToBase
    : undefined

  const submittedFactor = submittedUomSnapshot?.conversionFactorToBase
    ? Decimal.fromPB(submittedUomSnapshot.conversionFactorToBase)
    : undefined

  const baseQuantityToAdd = (() => {
    if (submittedFactor) {
      return convertUoMQuantityToQuantity(uomQuantityToAdd, submittedFactor)
    }
    if (existingTransferProduct) {
      return convertUoMQuantityToQuantity(uomQuantityToAdd, existingFactor)
    }
    const product = state.searchProduct?.product
    const uomId = getTransferDefaultUomId(product)
    if (!uomId) return uomQuantityToAdd

    const factor = getConversionFactorForUom(product, uomId)
    return convertUoMQuantityToQuantity(uomQuantityToAdd, factor)
  })()

  if (existingTransferProduct) {
    state.transferProductsById[payloadId] = {
      ...existingTransferProduct,
      position: Object.values(state.transferProductsById).length + 1,
      quantitySentV2:
        status === TransferStatus.SENT
          ? existingTransferProduct.quantitySentV2
          : (existingTransferProduct.quantitySentV2
              ? Decimal.fromPB(existingTransferProduct.quantitySentV2)
              : Decimal.zero()
            ).add(baseQuantityToAdd),
      quantityReceivedV2:
        status === TransferStatus.SENT
          ? (existingTransferProduct.quantityReceivedV2
              ? Decimal.fromPB(existingTransferProduct.quantityReceivedV2)
              : Decimal.zero()
            ).add(baseQuantityToAdd)
          : Decimal.zero(),
      quantityRequestedV2:
        status === TransferStatus.SENT
          ? existingTransferProduct.quantityRequestedV2
          : Decimal.fromPBOrZero(
              existingTransferProduct.quantityRequestedV2
            ).add(baseQuantityToAdd),
    }
  } else {
    state.transferProductsById[payloadId] = {
      productId: state.searchProduct.product.id,
      position: Object.values(state.transferProductsById).length + 1,
      quantitySentV2:
        status === TransferStatus.SENT ? Decimal.zero() : baseQuantityToAdd,
      quantityReceivedV2:
        status === TransferStatus.SENT ? baseQuantityToAdd : Decimal.zero(),
      quantityRequestedV2:
        status === TransferStatus.SENT ? Decimal.zero() : baseQuantityToAdd,
      isSpecialOrder: state.searchProduct.product.isSpecialOrderableV2,
      additionalFeesWhenReceived: [],
      uomId: submittedUomId,
      uomSnapshot: submittedUomSnapshot,
    }
  }

  // Keep UOM wrapper fields consistent with base quantities (and enforce snapshot policy).
  state.transferProductsById[payloadId] = applyTransferUomSelection({
    transferProduct: state.transferProductsById[payloadId],
  })

  state.transferProductsById = fixTransferProductsOrders(
    state.transferProductsById
  )

  state.productsById = {
    ...state.productsById,
    [UUID.fromPB(state.searchProduct.product.id).toString()]:
      state.searchProduct.product,
  }

  if (state.searchProduct.matchedCostProduct?.id) {
    state.productsById = {
      ...state.productsById,
      [UUID.fromPB(state.searchProduct.matchedCostProduct.id).toString()]:
        state.searchProduct.matchedCostProduct,
    }
  }

  if (state.searchProduct.matchedPriceProduct?.id) {
    state.productsById = {
      ...state.productsById,
      [UUID.fromPB(state.searchProduct.matchedPriceProduct.id).toString()]:
        state.searchProduct.matchedPriceProduct,
    }
  }

  if (state.searchProduct.catalogProduct) {
    state.catalogProductsById = {
      ...state.catalogProductsById,
      [UUID.fromPB(state.searchProduct.product.id).toString()]:
        state.searchProduct.catalogProduct,
    }
  }

  if (state.searchProduct.inventories) {
    state.inventoriesByProductId = {
      ...state.inventoriesByProductId,
      [UUID.fromPB(state.searchProduct.product.id).toString()]:
        state.searchProduct.inventories,
    }
  }

  state.addProductQuantity = Decimal.fromBigSource(1)
  delete state.searchProduct
  state.isEdited = checkTransferIsEdited(state)
}
